import { useRef, type RefObject } from 'react';
import type { useSortable } from '@dnd-kit/sortable';
import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { ChevronDownIcon, ChevronUpIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DragHandle } from '@/shared/ui';

type Sortable = ReturnType<typeof useSortable>;

export interface ApprovalChainStepRowHeaderProps {
  index: number;
  lastIndex: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canRemove: boolean;
  disabled: boolean;
  dragAttributes: Sortable['attributes'];
  dragListeners: Sortable['listeners'];
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

/** A step's drag handle, its number, and the buttons that move or remove it. */
export function ApprovalChainStepRowHeader(props: ApprovalChainStepRowHeaderProps) {
  const { t } = useTranslation('approvalChains');
  const { index, lastIndex, canMoveUp, canMoveDown, canRemove, disabled } = props;

  /*
   * A step that reaches either end disables the arrow that took it there, and a disabled
   * button drops focus to `<body>` — the keyboard user loses their place mid-reorder. Hand
   * focus to the opposite arrow when that is about to happen.
   */
  const moveUpRef = useRef<HTMLButtonElement>(null);
  const moveDownRef = useRef<HTMLButtonElement>(null);
  const keepFocus = (target: RefObject<HTMLButtonElement | null>, staysEnabled: boolean) => {
    if (staysEnabled) return;
    // After the field array re-renders, not before.
    requestAnimationFrame(() => target.current?.focus());
  };

  return (
    <div className="flex items-center gap-3">
      <DragHandle
        ariaLabel={t('manageApprovalChains.approvalChainStepRow.reorderStep', {
          step: index + 1,
        })}
        // Its own `title` rather than a Tooltip: DragHandle takes no ref and
        // spreads no rest props, so Radix's asChild clone would be dropped.
        title={t('manageApprovalChains.approvalChainStepRow.reorder')}
        attributes={props.dragAttributes}
        listeners={props.dragListeners}
        disabled={disabled}
      />
      <Badge variant="secondary" size="sm" roundness="full" className="shrink-0">
        {t('manageApprovalChains.approvalChainStepRow.stepNumber', { stepNumber: index + 1 })}
      </Badge>
      <div className="ml-auto flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manageApprovalChains.approvalChainStepRow.moveStepUp', {
                step: index + 1,
              })}
              disabled={disabled || !canMoveUp}
              ref={moveUpRef}
              onClick={() => {
                props.onMove(index, index - 1);
                keepFocus(moveDownRef, index - 1 > 0);
              }}
              data-testid="approval-chain-step-move-up"
            >
              <ChevronUpIcon aria-hidden className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('manageApprovalChains.approvalChainStepRow.moveUp')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manageApprovalChains.approvalChainStepRow.moveStepDown', {
                step: index + 1,
              })}
              disabled={disabled || !canMoveDown}
              ref={moveDownRef}
              onClick={() => {
                props.onMove(index, index + 1);
                keepFocus(moveUpRef, index + 1 < lastIndex);
              }}
              data-testid="approval-chain-step-move-down"
            >
              <ChevronDownIcon aria-hidden className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('manageApprovalChains.approvalChainStepRow.moveDown')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manageApprovalChains.approvalChainStepRow.removeStep', {
                step: index + 1,
              })}
              disabled={disabled || !canRemove}
              onClick={() => props.onRemove(index)}
              data-testid="approval-chain-step-remove"
            >
              <Trash2Icon aria-hidden className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('manageApprovalChains.approvalChainStepRow.remove')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
